import { ApiService } from '../../api/api-service';
import {
  AvailableParticipant,
  Conversation,
  ConversationDetail,
  ConversationFilterParams,
  Message,
  SendMessageRequest,
} from '../../types/messaging-types';

const SERVICE_ROOT = '/messaging';

/**
 * Service for messaging-related API calls and business logic
 */
export class MessagingService extends ApiService {
  /**
   * Get all conversations for the current user
   */
  async getConversations(): Promise<Conversation[]> {
    return this.handleApiCall(
      () => this.client.get<Conversation[]>(`${SERVICE_ROOT}/conversations`),
      'Error fetching conversations'
    );
  }

  /**
   * Get a specific conversation with paginated messages
   */
  async getConversationById(
    id: number,
    skip: number = 0,
    take: number = 50
  ): Promise<ConversationDetail> {
    return this.handleApiCall(
      () =>
        this.client.get<ConversationDetail>(`${SERVICE_ROOT}/conversations/${id}`, {
          params: { skip, take },
        }),
      `Error fetching conversation ${id}`
    );
  }

  /**
   * Create a new conversation or get existing one
   */
  async createOrGetConversation(participant2Id: number): Promise<Conversation> {
    return this.handleApiCall(
      () => this.client.post<Conversation>(`${SERVICE_ROOT}/conversations`, participant2Id),
      'Error creating conversation'
    );
  }

  /**
   * Send a message in a conversation
   */
  async sendMessage(conversationId: number, request: SendMessageRequest): Promise<Message> {
    return this.handleApiCall(
      () =>
        this.client.post<Message>(
          `${SERVICE_ROOT}/conversations/${conversationId}/messages`,
          request
        ),
      'Error sending message'
    );
  }

  /**
   * Mark all messages in a conversation as read
   */
  async markConversationAsRead(conversationId: number): Promise<void> {
    return this.handleApiCall(
      () => this.client.put(`${SERVICE_ROOT}/conversations/${conversationId}/read`),
      'Error marking conversation as read'
    );
  }

  /**
   * Search conversations
   */
  async searchConversations(searchTerm: string): Promise<Conversation[]> {
    return this.handleApiCall(
      () =>
        this.client.get<Conversation[]>(`${SERVICE_ROOT}/conversations/search`, {
          params: { searchTerm },
        }),
      'Error searching conversations'
    );
  }

  /**
   * Get unread message count
   */
  async getUnreadCount(): Promise<number> {
    return this.handleApiCall(async () => {
      const response = await this.client.get<{ count: number }>(`${SERVICE_ROOT}/unread-count`);
      return response.count;
    }, 'Error fetching unread count');
  }

  /**
   * Search conversations with filters (date range, supervisor/dealer filter)
   */
  async searchConversationsWithFilters(
    filterParams: ConversationFilterParams
  ): Promise<Conversation[]> {
    return this.handleApiCall(
      () => this.client.post<Conversation[]>(`${SERVICE_ROOT}/conversations/search`, filterParams),
      'Error searching conversations with filters'
    );
  }

  /**
   * Search for available participants to start a conversation with
   */
  async searchAvailableParticipants(
    searchTerm: string,
    dealerId?: number
  ): Promise<AvailableParticipant[]> {
    return this.handleApiCall(
      () =>
        this.client.get<AvailableParticipant[]>(`${SERVICE_ROOT}/participants/search`, {
          params: { searchTerm, ...(dealerId && { dealerId }) },
        }),
      'Error searching available participants'
    );
  }
}
